import React from 'react';
import { cn } from '../../utils/cn';

/**
 * Button Component
 *
 * A clean, reliable button with solid colors and smooth interactions.
 * Uses standard Tailwind classes for maximum compatibility.
 */

const variants = {
  primary: `
    bg-blue-600 hover:bg-blue-700 active:bg-blue-800
    text-white font-semibold
    shadow-md hover:shadow-lg
    border border-blue-600 hover:border-blue-700
  `,
  secondary: `
    bg-white text-gray-700
    border border-gray-200 hover:border-gray-300
    shadow-sm hover:shadow
    hover:bg-gray-50
    active:bg-gray-100
  `,
  ghost: `
    bg-transparent text-gray-600
    hover:bg-gray-100
    hover:text-gray-900
    active:bg-gray-200
  `,
  accent: `
    bg-amber-500 hover:bg-amber-600 active:bg-amber-700
    text-white font-semibold
    shadow-md hover:shadow-lg
  `,
  danger: `
    bg-red-500 hover:bg-red-600 active:bg-red-700
    text-white font-semibold
    shadow-md hover:shadow-lg
  `,
  outline: `
    bg-transparent text-blue-600
    border-2 border-blue-400 hover:border-blue-500
    hover:bg-blue-50
    active:bg-blue-100
  `,
  link: `
    bg-transparent text-blue-600
    hover:text-blue-700
    hover:underline underline-offset-4
    p-0 h-auto
  `,
};

const sizes = {
  xs: 'px-2.5 py-1.5 text-xs gap-1.5 rounded-md',
  sm: 'px-3.5 py-2 text-sm gap-2 rounded-lg',
  md: 'px-4 py-2.5 text-sm gap-2 rounded-lg',
  lg: 'px-5 py-3 text-base gap-2.5 rounded-xl',
  xl: 'px-6 py-3.5 text-lg gap-3 rounded-xl',
};

const iconSizes = {
  xs: 'w-3.5 h-3.5',
  sm: 'w-4 h-4',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
  xl: 'w-5 h-5',
};

const Spinner = ({ className }) => (
  <svg
    className={cn('animate-spin', className)}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  icon: Icon,
  iconRight: IconRight,
  loading = false,
  disabled = false,
  fullWidth = false,
  className,
  as: Component = 'button',
  ...props
}) {
  const isDisabled = disabled || loading;

  return (
    <Component
      className={cn(
        // Base styles
        'inline-flex items-center justify-center',
        'font-medium',
        'transition-all duration-150 ease-out',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
        'select-none whitespace-nowrap',

        // Variant styles
        variants[variant],

        // Size styles
        sizes[size],

        // States
        isDisabled && 'opacity-50 cursor-not-allowed pointer-events-none',
        fullWidth && 'w-full',

        // Subtle lift effect
        variant !== 'link' && variant !== 'ghost' && !isDisabled && [
          'hover:-translate-y-px',
          'active:translate-y-0',
        ],

        className
      )}
      disabled={isDisabled}
      {...props}
    >
      {loading ? (
        <Spinner className={iconSizes[size]} />
      ) : Icon ? (
        <Icon className={cn(iconSizes[size], 'opacity-80')} />
      ) : null}

      {children && <span>{children}</span>}

      {IconRight && !loading && (
        <IconRight className={cn(iconSizes[size], 'opacity-80')} />
      )}
    </Component>
  );
}

// Icon-only button variant
export function IconButton({
  icon: Icon,
  variant = 'ghost',
  size = 'md',
  label,
  className,
  ...props
}) {
  const iconButtonSizes = {
    xs: 'w-7 h-7',
    sm: 'w-8 h-8',
    md: 'w-9 h-9',
    lg: 'w-10 h-10',
    xl: 'w-12 h-12',
  };

  return (
    <Button
      variant={variant}
      className={cn(
        iconButtonSizes[size],
        'p-0 flex items-center justify-center',
        className
      )}
      aria-label={label}
      {...props}
    >
      <Icon className={iconSizes[size]} />
    </Button>
  );
}

export default Button;
